/**
 * Detection utilities for PC, Mobile/Tablet, and TV (Android TV & Fire TV Stick).
 */

export function detectIsTv(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  
  // Specific Android TV, Fire TV, Silk Browser, Smart TV User-Agent signatures
  const tvKeywords = [
    'aftt', // Amazon Fire TV Stick
    'aftm', // Amazon Fire TV Stick Lite/4K
    'aftb', // Amazon Fire TV Box
    'afts', // Amazon Fire TV
    'aft',  // Amazon Fire TV series
    'firetv',
    'silk', // Amazon Silk TV Browser
    'amazonwebappplatform',
    'android tv',
    'googletv',
    'google tv',
    'smart-tv',
    'smarttv',
    'tizen', // Samsung TV
    'web0s', // LG TV
    'webos',
    'appletv',
    'crkey', // Chromecast with Google TV
    'chromecast',
    'hbbtv',
    'viera', // Panasonic
    'netcast',
    'roku',
    'pov_tv',
    'mi tv',
    'mitv',
    'mibox',
    'bravia', // Sony TV
    'hisense',
    'philipstv',
    'shield', // Nvidia Shield TV
    'nexus player',
    'tvbox',
    'atv'
  ];

  const hasTvKeyword = tvKeywords.some(keyword => ua.includes(keyword));
  if (hasTvKeyword) return true;

  // Additional check: If standalone TV browser without touch, standard 16:9 1080p/4K TV ratio
  if (typeof window !== 'undefined' && window.matchMedia) {
    const isTvMedia = window.matchMedia('(tv), (display-mode: standalone) and (min-width: 960px)').matches;
    if (isTvMedia && !('ontouchstart' in window)) {
      return true;
    }
  }

  return false;
}

export function detectIsTouch(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore
    (navigator.msMaxTouchPoints && navigator.msMaxTouchPoints > 0)
  );
}

export function detectScreenCategory(): 'mobile' | 'tablet' | 'pc' {
  if (typeof window === 'undefined') return 'pc';
  const width = window.innerWidth;
  if (width < 640) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'pc';
}

/**
 * TV Remote and D-Pad Key Map definitions
 * Supports both modern KeyboardEvent.key strings and TV Remote keyCodes
 */
export const TV_KEYS = {
  UP: ['ArrowUp', 'Up', 'UIKeyInputUpArrow'],
  DOWN: ['ArrowDown', 'Down', 'UIKeyInputDownArrow'],
  LEFT: ['ArrowLeft', 'Left', 'UIKeyInputLeftArrow'],
  RIGHT: ['ArrowRight', 'Right', 'UIKeyInputRightArrow'],
  ENTER: ['Enter', 'Select', 'Accept', 'OK', 'Space'],
  BACK: ['Escape', 'Esc', 'GoBack', 'Back', 'BrowserBack', 'UIMenuBarItem'],
  PLAY_PAUSE: ['MediaPlayPause', 'MediaPlay', 'MediaPause', 'Play', 'Pause'],
  FAST_FORWARD: ['MediaFastForward', 'FastForward'],
  REWIND: ['MediaRewind', 'Rewind'],
  NEXT_TRACK: ['MediaTrackNext', 'NextTrack'],
  PREV_TRACK: ['MediaTrackPrevious', 'PreviousTrack'],
  CHANNEL_UP: ['ChannelUp', 'PageUp'],
  CHANNEL_DOWN: ['ChannelDown', 'PageDown'],
  MENU: ['ContextMenu', 'Menu', 'Settings']
};

export const TV_KEYCODES = {
  UP: [38, 19],
  DOWN: [40, 20],
  LEFT: [37, 21],
  RIGHT: [39, 22],
  ENTER: [13, 23, 66, 32],
  BACK: [27, 4, 10009],
  PLAY_PAUSE: [179, 85, 126, 127],
  FAST_FORWARD: [228, 90],
  REWIND: [227, 89],
  MENU: [93, 82, 178]
};

export function isDirectionalEvent(e: { key?: string; keyCode?: number; which?: number }): 'up' | 'down' | 'left' | 'right' | null {
  const key = e.key || '';
  const code = e.keyCode || e.which || 0;

  if (TV_KEYS.UP.includes(key) || TV_KEYCODES.UP.includes(code)) return 'up';
  if (TV_KEYS.DOWN.includes(key) || TV_KEYCODES.DOWN.includes(code)) return 'down';
  if (TV_KEYS.LEFT.includes(key) || TV_KEYCODES.LEFT.includes(code)) return 'left';
  if (TV_KEYS.RIGHT.includes(key) || TV_KEYCODES.RIGHT.includes(code)) return 'right';
  return null;
}

export function isDirectionalKey(key: string): boolean {
  return (
    TV_KEYS.UP.includes(key) ||
    TV_KEYS.DOWN.includes(key) ||
    TV_KEYS.LEFT.includes(key) ||
    TV_KEYS.RIGHT.includes(key)
  );
}

export function isEnterEvent(e: { key?: string; keyCode?: number; which?: number }): boolean {
  const key = e.key || '';
  const code = e.keyCode || e.which || 0;
  return TV_KEYS.ENTER.includes(key) || TV_KEYCODES.ENTER.includes(code);
}

export function isEnterKey(key: string): boolean {
  return TV_KEYS.ENTER.includes(key);
}

export function isBackEvent(e: { key?: string; keyCode?: number; which?: number }): boolean {
  const key = e.key || '';
  const code = e.keyCode || e.which || 0;
  return TV_KEYS.BACK.includes(key) || TV_KEYCODES.BACK.includes(code);
}

export function isBackKey(key: string): boolean {
  return TV_KEYS.BACK.includes(key);
}

export function isPlayPauseEvent(e: { key?: string; keyCode?: number; which?: number }): boolean {
  const key = e.key || '';
  const code = e.keyCode || e.which || 0;
  return TV_KEYS.PLAY_PAUSE.includes(key) || TV_KEYCODES.PLAY_PAUSE.includes(code);
}
